// frontend/src/marketing/ui/index.jsx
//
// Marketing design-system primitives. Small, presentational, prop-driven.
// Shared across all public pages so the look stays consistent.
import React from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

/** Join class names, dropping falsy values. */
export function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

/* ------------------------------------------------------------------ Container */
export function Container({ className, children }) {
  return (
    <div className={cx("mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8", className)}>
      {children}
    </div>
  );
}
Container.propTypes = { className: PropTypes.string, children: PropTypes.node };
Container.defaultProps = { className: "", children: null };

/* -------------------------------------------------------------------- Section */
export function Section({ className, muted, children, id }) {
  return (
    <section id={id} className={cx("py-16 sm:py-24", muted && "bg-gray-50", className)}>
      <Container>{children}</Container>
    </section>
  );
}
Section.propTypes = {
  className: PropTypes.string,
  muted: PropTypes.bool,
  children: PropTypes.node,
  id: PropTypes.string,
};
Section.defaultProps = { className: "", muted: false, children: null, id: undefined };

/* --------------------------------------------------------------------- Eyebrow */
export function Eyebrow({ children, className }) {
  return (
    <p className={cx("text-sm font-semibold uppercase tracking-wide text-brand-600", className)}>
      {children}
    </p>
  );
}
Eyebrow.propTypes = { children: PropTypes.node, className: PropTypes.string };
Eyebrow.defaultProps = { children: null, className: "" };

/* ----------------------------------------------------------------------- Badge */
export function Badge({ children, tone, className }) {
  const tones = {
    brand: "bg-brand-50 text-brand-700 ring-brand-200",
    accent: "bg-accent-50 text-accent-700 ring-accent-100",
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset",
        tones[tone] || tones.brand,
        className,
      )}
    >
      {children}
    </span>
  );
}
Badge.propTypes = {
  children: PropTypes.node,
  tone: PropTypes.oneOf(["brand", "accent", "gray"]),
  className: PropTypes.string,
};
Badge.defaultProps = { children: null, tone: "brand", className: "" };

/* ---------------------------------------------------------------------- Button */
const BUTTON_VARIANTS = {
  primary:
    "bg-brand-600 text-white shadow-sm hover:bg-brand-700 focus-visible:outline-brand-600",
  secondary:
    "bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-gray-400",
  ghost:
    "text-gray-700 hover:text-brand-700 hover:bg-brand-50 focus-visible:outline-brand-600",
};
const BUTTON_SIZES = {
  md: "px-4 py-2 text-sm",
  lg: "px-5 py-3 text-base",
};

/**
 * Button — renders a router <Link> when `to` is set, an <a> when `href` is set,
 * else a native <button>. One styling source for all CTAs.
 */
export function Button({ to, href, variant, size, className, children, ...rest }) {
  const classes = cx(
    "inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
    BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary,
    BUTTON_SIZES[size] || BUTTON_SIZES.md,
    className,
  );
  if (to) {
    return (
      <Link to={to} className={classes} {...rest}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={classes} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={classes} {...rest}>
      {children}
    </button>
  );
}
Button.propTypes = {
  to: PropTypes.string,
  href: PropTypes.string,
  variant: PropTypes.oneOf(["primary", "secondary", "ghost"]),
  size: PropTypes.oneOf(["md", "lg"]),
  className: PropTypes.string,
  children: PropTypes.node,
};
Button.defaultProps = {
  to: undefined,
  href: undefined,
  variant: "primary",
  size: "md",
  className: "",
  children: null,
};
